import React, { useState, useCallback, useRef, useEffect } from 'react';  // eslint-disable-line @typescript-eslint/no-unused-vars
import { useLocation, useNavigate } from 'react-router-dom';
import GeneratedImagesInline, {
  type CatalogoMeta,
} from '../components/GeneratedImagesInline';
import SubscriptionLimitModal from '../components/SubscriptionLimitModal';
import { mergeLastCrearAnuncioState, loadLastCrearAnuncioState } from '../utils/crearAnuncioStorage';
import { supabase } from '../lib/supabase';
import { GENERATE_AD_URL, GENERATE_AUTH_TOKEN } from '../config';
import { GenerateAdError } from '../lib/generateAdRequest';
import { generateClassicImages } from './v2/imageGenerationRecovery';
import { useUserGroup } from '../hooks/useUserGroup';
import { keepGeneratingStudioPath } from '../lib/userGroups';

const GeneratedImagesPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get imageIds, companyId, lastPayload, and preloadedImages from location state or query params
  type PreloadedImage = { id: number; fileName: string; signedUrl: string; favorito: boolean };
  // Note: imageIds may be numbers OR UUID strings depending on the caller.
  // We keep them as-is (using 'any' cast) since they're used opaquely in Supabase .eq() calls.
  const state = location.state as { imageIds?: number[]; companyId?: string; lastPayload?: Record<string, any>; preloadedImages?: PreloadedImage[]; isOnboarding?: boolean; fromHistory?: boolean; sourceImageId?: string; catalogo?: CatalogoMeta; autoCarrusel?: boolean; autoCarruselCount?: number; autoRegenerar?: boolean; openDetailImageId?: string } | null;
  const params = new URLSearchParams(location.search);
  
  const initialImageIds = state?.imageIds || params.get('ids')?.split(',').map(Number).filter(Boolean) || [];
  const initialCompanyId = state?.companyId || params.get('companyId') || '';
  const initialLastPayload = state?.lastPayload || null;
  const initialPreloadedImages = state?.preloadedImages || null;
  // `isOnboarding` se captura UNA sola vez al montar (igual que imageIds/companyId).
  // No se re-deriva de location.state en cada render porque al abrir el detalle
  // mobile y volver atrás, los `window.history.pushState` crudos (guard de
  // onboarding + detail view) no preservan el `state` de React Router → al
  // hacer popstate, location.state vuelve null y el flag se perdía, mostrando
  // el layout estándar (header "Últimas generadas" + barra Listo) en vez del
  // de onboarding. Mantenerlo en useState lo hace estable durante todo el mount.
  const [isOnboarding] = useState(state?.isOnboarding === true);
  const fromHistory = state?.fromHistory === true;
  const sourceImageId = state?.sourceImageId || null;
  // "Generar producción": al montar este inline disparamos la generación
  // de carrusel automáticamente (lo maneja GeneratedImagesInline).
  const autoCarrusel = state?.autoCarrusel === true;
  // Cuántas imágenes pide el carrusel automático (stepper "x imágenes" − 1).
  const autoCarruselCount =
    typeof state?.autoCarruselCount === 'number' ? state.autoCarruselCount : 2;
  // El editor sugirió regenerar (el pedido del usuario no era editable) y el usuario
  // aceptó: al montar se dispara "Regenerar" solo.
  // Ver ADR-2026-08-12-clasificador-sugerir-regenerar.
  //
  // Se CAPTURA al montar (useState, no derivado de `state`) porque abajo se limpia el
  // flag del history-state: si el valor se recalculara, esa limpieza apagaría el
  // disparo antes de que el inline lo consuma.
  const [autoRegenerar] = useState<boolean>(state?.autoRegenerar === true);

  const [imageIds, setImageIds] = useState<number[]>(initialImageIds);
  const [companyId, setCompanyId] = useState<string>(initialCompanyId);
  const [lastPayload] = useState<Record<string, any> | null>(initialLastPayload);
  const [showSubscriptionLimitModal, setShowSubscriptionLimitModal] = useState<boolean>(false);
  const [subscriptionLimitType, setSubscriptionLimitType] = useState<'free-trial-ended' | 'plan-limit-reached'>('free-trial-ended');
  // Ref to block rapid clicks before React re-renders
  const generatingLockRef = useRef<boolean>(false);

  // El flag de auto-regenerar se consume UNA sola vez: se limpia del history-state en
  // cuanto se leyó. El editor navega acá con `replace: true`, así que sin esto el flag
  // quedaría pegado a la entrada del historial y un "atrás" del navegador dispararía
  // otra regeneración — gastando créditos que el usuario no pidió.
  useEffect(() => {
    if (!autoRegenerar) return;
    navigate(location.pathname, {
      state: { ...(state ?? {}), autoRegenerar: false },
      replace: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRegenerar]);

  // Cuando se navega a ESTA MISMA ruta con una nueva tanda de imágenes (ej.
  // "Convertir en anuncio" disparado desde el propio inline / lightbox de
  // GeneratedImagesInline, que ya vive en esta página), React Router NO
  // remonta el componente → el `useState` inicial no se re-ejecuta y los
  // imageIds quedan en la tanda vieja, así que la imagen recién generada no
  // aparece. Reaccionamos al cambio de navegación (location.key cambia en cada
  // navigate, incluso a la misma ruta) para tomar los ids nuevos y mostrarlos.
  useEffect(() => {
    const s = location.state as { imageIds?: number[]; companyId?: string } | null;
    if (s?.imageIds && s.imageIds.length > 0) {
      setImageIds(s.imageIds);
      if (s.companyId) setCompanyId(s.companyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const checkSubscriptionLimits = async (): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!profile?.id_company) throw new Error('No company associated with user');

      const { data: companyProfile } = await supabase
        .from('company_profiles')
        .select('plan, imagenes_restantes')
        .eq('id_company', profile.id_company)
        .single();

      if (!companyProfile) throw new Error('No company profile found');

      const remaining = (companyProfile as any).imagenes_restantes as number | null | undefined;
      if (typeof remaining === 'number' && remaining <= 0) {
        const isFree = (companyProfile as any).plan === 'gratis';
        setSubscriptionLimitType(isFree ? 'free-trial-ended' : 'plan-limit-reached');
        setShowSubscriptionLimitModal(true);
        return false;
      }

      return true;
    } catch (err) {
      console.error('Error checking subscription limits:', err);
      return true;
    }
  };

  // Salida del flujo de onboarding (cerrar / volver / back del navegador).
  // Debe coincidir con el destino de los CTAs "Crear más imágenes" que vienen
  // dentro de `GeneratedImagesInline` (usan `showsV2Studio`): dev y Grupo B
  // caen en la home V2, Grupo A en la clásica. Antes era una constante fija a
  // la home clásica, así que un usuario de Grupo B que salía con el back del
  // navegador terminaba en la interfaz vieja. Ver ADR-678.
  const { isDevTester, grupo } = useUserGroup();
  const onboardingDest = keepGeneratingStudioPath(isDevTester, grupo);
  // El guard de `popstate` se registra una sola vez al montar; el grupo puede
  // resolverse después (fetch de `useUserGroup`). Un ref mantiene el destino
  // fresco sin re-ejecutar el efecto — si lo pusiéramos en las deps, cada
  // cambio de grupo empujaría otra entrada dummy al history.
  const onboardingDestRef = useRef(onboardingDest);
  onboardingDestRef.current = onboardingDest;

  const handleClose = () => {
    if (fromHistory) {
      navigate('/redes-sociales/imagenes', { state: { expandImageId: sourceImageId } });
      return;
    }
    if (isOnboarding) {
      navigate(onboardingDest, { replace: true });
      return;
    }
    // Clear selected products when closing image results
    mergeLastCrearAnuncioState({ selectedVariantIds: [] });
    navigate(-1);
  };

  const handleReturnToConfig = () => {
    if (fromHistory) {
      navigate('/redes-sociales/imagenes', { state: { expandImageId: sourceImageId } });
      return;
    }
    if (isOnboarding) {
      navigate(onboardingDest, { replace: true });
      return;
    }
    navigate(-1);
  };

  // Intercept browser back button for onboarding flow
  useEffect(() => {
    if (!isOnboarding) return;
    // Push a dummy entry so pressing back stays on this page first
    window.history.pushState({ onboardingGuard: true }, '');
    const onPopState = (e: PopStateEvent) => {
      // Redirect to the user's studio home instead of going back to AddFirstProduct
      navigate(onboardingDestRef.current, { replace: true });
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [isOnboarding, navigate]);

  const getFormatName = (formato: string): string => {
    switch (formato) {
      case 'story': return '9:16';
      case 'square': return '1:1';
      case 'post':
      default: return '4:5';
    }
  };

  const handleGenerateMore = useCallback(async (): Promise<{ imageIds?: number[]; companyId?: string } | void> => {
    // Immediate lock to prevent rapid clicks
    if (generatingLockRef.current) return;
    generatingLockRef.current = true;

    try {
      // Check subscription limits before generating
      const canGenerate = await checkSubscriptionLimits();
      if (!canGenerate) { generatingLockRef.current = false; return; }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');

      let payload: Record<string, any>;

      // If we have lastPayload from navigation state, use it (from PruebaNuevaInterfaz)
      if (lastPayload) {
        payload = {
          ...lastPayload,
          user_id: user.id,
          cantidad_imagenes: 2, // Generate 2 more
        };
      } else {
        // Fallback to building payload from saved state (for other flows)
        const last = loadLastCrearAnuncioState();
        if (!last) throw new Error('No hay configuración previa');

        const variantIds = (last.selectedVariantIds || []).map((s) => String(s)).filter(Boolean);
        if (variantIds.length === 0) throw new Error('No hay productos seleccionados');

        const modo = last.modo || 'rapido';

        const FASHION_LABEL = 'Moda e Indumentaria';
        const isFashion = ((last.rubro as string | undefined) ?? FASHION_LABEL) === FASHION_LABEL;
        const compose = (!isFashion && modo === 'personalizado')
          ? 'producto'
          : ((last.composicion === 'modelo' || last.composicion === 'producto') ? last.composicion : 'producto');
        const formato = (last.formato === 'post' || last.formato === 'story' || last.formato === 'square') ? last.formato : 'post';
        const formatName = getFormatName(formato);
        const mode = variantIds.length > 1 ? 'multiple' : 'single';

        payload = {
          user_id: user.id,
          variante_id: variantIds,
          mode,
          modo,
          estilo: 'automatico',
          tipo: 'imagen',
          cantidad_imagenes: 2,
          Format: formatName,
          objetivo: last.objetivo || undefined,
          detalles_oferta: last.detallesOferta?.trim() ? last.detallesOferta.trim() : undefined,
          composicion: compose,
        };

        if (last.pose) payload.pose = last.pose;

        // Background
        const bg = last.background;
        const bgMode = bg?.mode || 'blanco';
        payload.background_mode = bgMode;
        if (bgMode === 'elegido') {
          if (typeof bg?.id === 'number' && Number.isFinite(bg.id)) payload.background_id = bg.id;
          if (bg?.url && !(typeof bg?.id === 'number' && Number.isFinite(bg.id))) payload.background_url = bg.url;
        } else if (bgMode === 'blanco' && modo === 'personalizado') {
          payload.background_id = 5;
        }
        if (bgMode === 'descripcion' && bg?.text?.trim()) payload.background_text = bg.text.trim();

        // Composition
        const comp = last.composition;
        if (compose === 'modelo' && comp) {
          payload.composition_mode = comp.enabled ? true : false;
          if (comp.enabled) {
            if (typeof comp.selectedCompId === 'number' && Number.isFinite(comp.selectedCompId)) payload.composition_id = comp.selectedCompId;
            if (!payload.composition_id && comp.selectedCompUrl) payload.composition_url = comp.selectedCompUrl;
            if (comp.compositionText && comp.compositionText.trim()) payload.composition_text = comp.compositionText.trim();
          }
        }

        // Modelo (model customization)
        const modelo = last.modelo;
        if (compose === 'modelo' && modelo?.mode) {
          payload.modelo_mode = modelo.mode;
          if (modelo.mode === 'guardados' && modelo.guardadoId) {
            payload.modelo_guardado_id = modelo.guardadoId;
            if (modelo.guardadoNombre) payload.modelo_guardado_nombre = modelo.guardadoNombre;
          } else if (modelo.mode === 'personalizado') {
            if (modelo.genero) payload.genero = modelo.genero;
            if (modelo.edad) payload.edad = modelo.edad;
            if (modelo.tipoCuerpo) payload.tipo_cuerpo = modelo.tipoCuerpo;
            if (modelo.tonoPiel) payload.tono_piel = modelo.tonoPiel;
            if (modelo.colorPelo) payload.color_pelo = modelo.colorPelo;
            if (modelo.largoPelo) payload.largo_pelo = modelo.largoPelo;
            if (modelo.descripcion?.trim()) payload.desc_modelo = modelo.descripcion.trim();
          }
        }

        // Modo Pro
        payload.modo_pro = last.modoPro === true;
      }

      console.log('[GeneratedImagesPage] handleGenerateMore payload', payload);

      // Carrera respuesta-vs-poll: el backend nuevo puede responder 202 sin
      // ids (generación lenta); el poll por execution_id las recupera. Se
      // sintetiza `data` con la misma shape para no tocar el flujo de abajo.
      const genResult = await generateClassicImages({
        payload,
        companyId,
        expectedCount:
          payload.tipo === 'imagen'
            ? (payload.cantidad_imagenes as number | undefined)
            : undefined,
      });
      const data: any = {
        imagenes_generadas_ids: genResult.imageIds,
        id_company: genResult.companyId,
      };
      if (Array.isArray(data?.imagenes_generadas_ids) && data.imagenes_generadas_ids.length > 0) {
        const newIds = data.imagenes_generadas_ids as number[];
        const newCompanyId = (data.id_company as string) || companyId;
        
        generatingLockRef.current = false;
        // Return data so the child component can handle display inline
        // Do NOT update imageIds — that changes the prop and triggers a full re-fetch
        return { imageIds: newIds, companyId: newCompanyId };
      } else {
        throw new Error('No se recibieron imágenes generadas.');
      }
    } catch (e: any) {
      console.error('[GeneratedImagesPage] handleGenerateMore error', e);
      generatingLockRef.current = false;
    }
  }, [companyId, lastPayload]);

  if (imageIds.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-surface-bg">
        <div className="text-center">
          <h2 className="text-[17px] font-semibold text-brand-black/80">No hay imágenes para mostrar</h2>
          <button
            onClick={() => navigate('/')}
            className="mt-4 px-5 py-2.5 bg-brand-primary text-white text-[15px] font-semibold rounded-md hover:bg-brand-primary-hover shadow-sm transition-colors"
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <GeneratedImagesInline
        imageIds={imageIds as any}
        companyId={companyId}
        onClose={handleClose}
        onReturnToConfig={handleReturnToConfig}
        onGenerateMore={handleGenerateMore}
        onReady={() => {}}
        preloadedImages={initialPreloadedImages as any}
        isOnboarding={isOnboarding}
        catalogo={state?.catalogo}
        autoCarrusel={autoCarrusel}
        autoCarruselCount={autoCarruselCount}
        autoRegenerar={autoRegenerar}
        // Se lo pasamos para que el inline lo reenvíe al editor: si desde allá se
        // acepta regenerar, vuelve y `handleGenerateMore` regenera ESTA tanda.
        lastPayload={lastPayload}
        openDetailImageId={state?.openDetailImageId}
        // La tanda vino del modo AUTOMÁTICO de Interfaz V2 (payload con
        // modo 'rapido') → "Seguir generando"/"Listo" promueven "Imagen
        // personalizada" al frente de la fila de modos de la home V2.
        promoteModeloFirstOnHome={lastPayload?.modo === 'rapido'}
      />
      <SubscriptionLimitModal
        isOpen={showSubscriptionLimitModal}
        type={subscriptionLimitType}
        onClose={() => setShowSubscriptionLimitModal(false)}
      />
    </>
  );
};

export default GeneratedImagesPage;
